import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Box as MuiBox,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { employeeApi } from '../services/employeeService.ts';
import { tipoutApi, BulkTipOutRequest } from '../services/tipoutService.ts';

interface TipOutEntry {
  id: string;
  employeeName: string;
  position: 'Server' | 'Host' | 'Bar';
  netSales: number;
  bar1Percent: number;
  bus1_5Percent: number;
  expoAmount: number;
  totalTipOut: number;
  shift: 'AM' | 'PM' | 'Event1' | 'Event2';
  section: 'Daily' | 'Event';
}

interface TipOutData {
  daily: {
    am: TipOutEntry[];
    pm: TipOutEntry[];
  };
  event: {
    event1: TipOutEntry[];
    event2: TipOutEntry[];
  };
}

const TipOutLogForm: React.FC = () => {
  const { showSuccess, showError } = useSnackbar();
  const { sessionToken } = useAuth();
  const [tipOutData, setTipOutData] = useState<TipOutData>({
    daily: { am: [], pm: [] },
    event: { event1: [], event2: [] }
  });
  
  // Employee data from API
  const [employees, setEmployees] = useState<string[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeeNameToCode, setEmployeeNameToCode] = useState<Record<string, string>>({});
  const [employeeNameToId, setEmployeeNameToId] = useState<Record<string, number>>({});
  const [employeeIdToName, setEmployeeIdToName] = useState<Record<number, string>>({});
  
  // Inline form state
  const [inlineFormData, setInlineFormData] = useState({
    am: {
      type: 'Server' as 'Server' | 'Host' | 'Bar',
      employee: '',
      netSales: '',
      bar1Percent: '',
      bus1_5Percent: '',
      expo: '0',
    },
    pm: {
      type: 'Server' as 'Server' | 'Host' | 'Bar',
      employee: '',
      netSales: '',
      bar1Percent: '',
      bus1_5Percent: '',
      expo: '0',
    },
    event1: {
      type: 'Server' as 'Server' | 'Host' | 'Bar',
      employee: '',
      netSales: '',
      bar1Percent: '',
      bus1_5Percent: '',
      expo: '0',
    },
    event2: {
      type: 'Server' as 'Server' | 'Host' | 'Bar',
      employee: '',
      netSales: '',
      bar1Percent: '',
      bus1_5Percent: '',
      expo: '0',
    }
  });
  
  // Dialog form state
  const [formData, setFormData] = useState({
    employeeName: '',
    position: 'Server' as 'Server' | 'Host' | 'Bar',
    netSales: '',
    bar1Percent: '',
    bus1_5Percent: '',
    expoAmount: '',
    shift: 'AM' as 'AM' | 'PM',
    section: 'Daily' as 'Daily' | 'Event',
  });
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TipOutEntry | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Save loading state
  const [saveLoading, setSaveLoading] = useState(false);
  
  // Inline editing state
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [unsavedChanges, setUnsavedChanges] = useState<Set<string>>(new Set());
  const [tempRowData, setTempRowData] = useState<Partial<TipOutEntry> | null>(null);
  
  // Current date state
  const [currentDate, setCurrentDate] = useState<string>('');
  const [dateLoading, setDateLoading] = useState(false);
  
  // Data loading state
  const [dataLoading, setDataLoading] = useState(false);

  // Auto-save state - the whole day's entries are saved as one bulk request, debounced
  // after any add/edit/delete. The initial load (fetchExistingData) must not trigger it.
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Convert TipOutEntry to API format
  const convertToApiFormat = (entry: TipOutEntry) => {
    // Find employee ID by name using the actual database ID mapping
    const employeeId = employeeNameToId[entry.employeeName] || 0; // Use 0 as fallback if not found
    
    if (employeeId === 0) {
      console.warn(`Employee not found: ${entry.employeeName}`);
    }
    
    return {
      employeeId: employeeId,
      type: entry.position.toLowerCase() as 'server' | 'host' | 'bar',
      eventType: entry.section.toLowerCase() as 'daily' | 'event',
      shift: entry.shift,
      netSales: entry.netSales,
      bar: entry.bar1Percent,
      bus: entry.bus1_5Percent,
      expo: entry.expoAmount,
      totalTipOut: entry.totalTipOut,
    };
  };

  // Convert API data to TipOutEntry format
  const convertFromApiFormat = (apiLog: any): TipOutEntry => {
    // Find employee name by ID using the actual database ID mapping
    const employeeName = employeeIdToName[apiLog.employeeId] || `Employee ${apiLog.employeeId}`;
    
    return {
      id: apiLog.id?.toString() || Date.now().toString(),
      employeeName: employeeName,
      position: apiLog.type.charAt(0).toUpperCase() + apiLog.type.slice(1) as 'Server' | 'Host' | 'Bar',
      netSales: Number(apiLog.netSales) || 0,
      bar1Percent: Number(apiLog.bar) || 0,
      bus1_5Percent: Number(apiLog.bus) || 0,
      expoAmount: Number(apiLog.expo) || 0,
      totalTipOut: Number(apiLog.totalTipOut) || 0,
      section: apiLog.eventType.charAt(0).toUpperCase() + apiLog.eventType.slice(1) as 'Daily' | 'Event',
      shift: apiLog.shift,
    };
  };

  // Inline editing handlers
  const handleStartEdit = (entry: TipOutEntry) => {
    setEditingRowId(entry.id);
    setTempRowData({ ...entry });
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setTempRowData(null);
  };

  const handleTempDataChange = (field: keyof TipOutEntry, value: string | number) => {
    if (!tempRowData) return;
    
    const updatedData = { ...tempRowData, [field]: value };
    
    // Auto-calculate bar and bus if netSales changes
    if (field === 'netSales') {
      const netSales = typeof value === 'string' ? parseFloat(value) || 0 : value;
      updatedData.bar1Percent = calculateBar1Percent(netSales);
      updatedData.bus1_5Percent = calculateBus1_5Percent(netSales);
      updatedData.totalTipOut = calculateTotalTipOut(updatedData.bar1Percent, updatedData.bus1_5Percent, updatedData.expoAmount || 0);
    } else if (field === 'expoAmount') {
      const expo = typeof value === 'string' ? parseFloat(value) || 0 : value;
      updatedData.totalTipOut = calculateTotalTipOut(updatedData.bar1Percent || 0, updatedData.bus1_5Percent || 0, expo);
    } else if (field === 'bar1Percent' || field === 'bus1_5Percent') {
      // Recalculate total when bar or bus changes using the actual values
      const bar = field === 'bar1Percent' ? (typeof value === 'string' ? parseFloat(value) || 0 : value) : (updatedData.bar1Percent || 0);
      const bus = field === 'bus1_5Percent' ? (typeof value === 'string' ? parseFloat(value) || 0 : value) : (updatedData.bus1_5Percent || 0);
      const expo = updatedData.expoAmount || 0;
      updatedData.totalTipOut = calculateTotalTipOut(bar, bus, expo);
    }
    
    setTempRowData(updatedData);
  };

  const handleSaveEdit = (entryId: string) => {
    if (!tempRowData) return;

    setTipOutData(prev => {
      const newData = { ...prev };
      
      // Find and update the entry in the correct location
      const updateEntryInArray = (entries: TipOutEntry[]) => {
        return entries.map(entry => 
          entry.id === entryId ? { ...entry, ...tempRowData } : entry
        );
      };

      // Update in the correct array based on section and shift
      if (tempRowData.section === 'Daily') {
        if (tempRowData.shift === 'AM') {
          newData.daily.am = updateEntryInArray(newData.daily.am);
        } else if (tempRowData.shift === 'PM') {
          newData.daily.pm = updateEntryInArray(newData.daily.pm);
        }
      } else if (tempRowData.section === 'Event') {
        if (tempRowData.shift === 'Event1') {
          newData.event.event1 = updateEntryInArray(newData.event.event1);
        } else if (tempRowData.shift === 'Event2') {
          newData.event.event2 = updateEntryInArray(newData.event.event2);
        }
      }

      return newData;
    });

    // Mark as unsaved
    setUnsavedChanges(prev => new Set(prev).add(entryId));
    
    setEditingRowId(null);
    setTempRowData(null);
  };

  const handleDeleteEntry = (id: string, section: 'Daily' | 'Event', shift?: 'AM' | 'PM' | 'event1' | 'event2') => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      setTipOutData(prev => {
        const newData = { ...prev };
        if (section === 'Daily' && shift && (shift === 'AM' || shift === 'PM')) {
          newData.daily[shift.toLowerCase() as 'am' | 'pm'] = newData.daily[shift.toLowerCase() as 'am' | 'pm'].filter(entry => entry.id !== id);
        } else if (section === 'Event' && shift && (shift === 'event1' || shift === 'event2')) {
          newData.event[shift] = newData.event[shift].filter(entry => entry.id !== id);
        }
        return newData;
      });
      
      // Remove from unsaved changes
      setUnsavedChanges(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      
      showSuccess('Entry deleted successfully!');
    }
  };

  // Shared save path for both the manual "Save Daily" button and auto-save. `silent`
  // suppresses toasts/errors in favor of the auto-save status indicator, and treats an
  // empty day as a normal no-op rather than a user-facing error.
  const saveAllEntries = async (silent: boolean): Promise<void> => {
    if (!sessionToken) {
      if (!silent) showError('No session token available');
      return;
    }

    const amEntries = tipOutData.daily.am.filter(entry => entry.employeeName !== 'TOTAL');
    const pmEntries = tipOutData.daily.pm.filter(entry => entry.employeeName !== 'TOTAL');
    const event1Entries = tipOutData.event.event1.filter(entry => entry.employeeName !== 'TOTAL');
    const event2Entries = tipOutData.event.event2.filter(entry => entry.employeeName !== 'TOTAL');
    const allEntries = [...amEntries, ...pmEntries, ...event1Entries, ...event2Entries];

    if (allEntries.length === 0) {
      if (!silent) showError('No data to save');
      return;
    }

    try {
      const logs = allEntries.map(convertToApiFormat);
      const requestData: BulkTipOutRequest = { logs };

      await tipoutApi.saveTipOutLogs(requestData, sessionToken);

      setUnsavedChanges(new Set());

      if (silent) {
        setAutoSaveStatus('saved');
      } else {
        showSuccess('Tip-out data saved successfully!');
      }
    } catch (error) {
      console.error('Error saving tip-out data:', error);
      const message = error instanceof Error ? error.message : 'Failed to save tip-out data';
      if (silent) {
        setAutoSaveStatus('error');
        setAutoSaveError(message);
      } else {
        showError(message);
      }
    }
  };

  const performAutoSave = useCallback(async () => {
    setAutoSaveStatus('saving');
    await saveAllEntries(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipOutData, sessionToken]);

  // Debounce a bulk auto-save ~900ms after any add/edit/delete - but not the initial
  // load from fetchExistingData, which also calls setTipOutData.
  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    setAutoSaveStatus('pending');
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveTimeoutRef.current = null;
      performAutoSave();
    }, 900);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipOutData]);

  // Save all data (Daily AM, PM + Event 1, Event 2) to API
  const handleSaveDaily = async () => {
    setSaveLoading(true);
    try {
      await saveAllEntries(false);
    } finally {
      setSaveLoading(false);
    }
  };

  // Fetch current date from API
  const fetchCurrentDate = async () => {
    if (!sessionToken) return;
    
    try {
      setDateLoading(true);
      const response = await tipoutApi.getCurrentDate(sessionToken);
      setCurrentDate(response.date);
    } catch (error) {
      console.error('Error fetching current date:', error);
      showError('Failed to fetch current date');
    } finally {
      setDateLoading(false);
    }
  };

  // Fetch existing tip-out data from API
  const fetchExistingData = async () => {
    if (!sessionToken) return;
    
    try {
      setDataLoading(true);
      const response = await tipoutApi.getCurrentDateLogs(sessionToken);
      
      // Convert API data to frontend format
      const convertedData = response.map(convertFromApiFormat);
      
      // Group data by section and shift
      const dailyAM: TipOutEntry[] = [];
      const dailyPM: TipOutEntry[] = [];
      const event1: TipOutEntry[] = [];
      const event2: TipOutEntry[] = [];
      
      convertedData.forEach(entry => {
        if (entry.section === 'Daily') {
          if (entry.shift === 'AM') {
            dailyAM.push(entry);
          } else if (entry.shift === 'PM') {
            dailyPM.push(entry);
          }
        } else if (entry.section === 'Event') {
          if (entry.shift === 'Event1') {
            event1.push(entry);
          } else if (entry.shift === 'Event2') {
            event2.push(entry);
          }
        }
      });
      
      // Update tip-out data with fetched data
      setTipOutData({
        daily: {
          am: dailyAM,
          pm: dailyPM,
        },
        event: {
          event1: event1,
          event2: event2,
        },
      });
      
    } catch (error) {
      console.error('Error fetching existing data:', error);
      showError('Failed to fetch existing tip-out data');
    } finally {
      setDataLoading(false);
    }
  };

  // Fetch employees from API
  const fetchEmployees = async () => {
    if (!sessionToken) return;
    
    setEmployeesLoading(true);
    try {
      const response = await employeeApi.getEmployees({ page: 1, limit: 100 }, sessionToken);
      const employeeNames = response.data.map(employee => employee.name);
      setEmployees(employeeNames);
      
      // Create mapping from name to employee code (for display)
      const nameToCode: Record<string, string> = {};
      // Create mapping from name to database ID (for API calls)
      const nameToId: Record<string, number> = {};
      // Create reverse mapping from database ID to name (for loading data)
      const idToName: Record<number, string> = {};
      
      response.data.forEach(emp => {
        nameToCode[emp.name] = (emp.employeeId ?? '0');
        nameToId[emp.name] = emp.id; // Use actual database ID
        idToName[emp.id] = emp.name; // Reverse mapping
      });
      
      setEmployeeNameToCode(nameToCode);
      setEmployeeNameToId(nameToId);
      setEmployeeIdToName(idToName);
    } catch (err) {
      showError('Failed to load employees');
      // Fallback to empty arrays/objects if API fails
      setEmployees([]);
      setEmployeeNameToCode({});
      setEmployeeNameToId({});
      setEmployeeIdToName({});
    } finally {
      setEmployeesLoading(false);
    }
  };

  // Load employees and current date on component mount
  useEffect(() => {
    fetchEmployees();
    fetchCurrentDate();
  }, [sessionToken]);

  // Load existing data after employees are loaded
  useEffect(() => {
    if (employees.length > 0 && sessionToken) {
      fetchExistingData();
    }
  }, [employees, sessionToken]);

  const positions = ['Server', 'Host', 'Bar'];
  const shifts = ['AM', 'PM'];
  const sections = ['Daily', 'Event'];

  // Shared across the AM/PM/Event tables and their combined-total rows (separate <Table>
  // elements) so their columns land at the same x-position and the totals line up under
  // the matching headers above them.
  const TIPOUT_COLUMN_WIDTHS = ['24%', '13%', '12%', '12%', '15%', '14%', '10%'];
  const TIPOUT_BORDER = '2px solid #7f0000';
  const TIPOUT_BODY_BORDER = '2px solid #9e9e9e';

  // Helper functions for calculations
  const calculateBar1Percent = (netSales: number) => {
    return Math.ceil(netSales * 0.01); // Round up to nearest dollar (no cents)
  };

  const calculateBus1_5Percent = (netSales: number) => {
    return Math.ceil(netSales * 0.015); // Round up to nearest dollar (no cents) - 1.5%
  };

  const calculateTotalTipOut = (bar: number, bus: number, expo: number) => {
    return bar + bus + expo;
  };

  // Calculate totals for a given set of entries
  const calculateTotals = (entries: TipOutEntry[]) => {
    return entries.reduce((totals, entry) => ({
      netSales: totals.netSales + entry.netSales,
      bar1Percent: totals.bar1Percent + entry.bar1Percent,
      bus1_5Percent: totals.bus1_5Percent + entry.bus1_5Percent,
      expoAmount: totals.expoAmount + entry.expoAmount,
      totalTipOut: totals.totalTipOut + entry.totalTipOut,
    }), {
      netSales: 0,
      bar1Percent: 0,
      bus1_5Percent: 0,
      expoAmount: 0,
      totalTipOut: 0,
    });
  };

  // Calculate combined totals for AM and PM shifts
  const calculateCombinedTotals = () => {
    const amTotals = calculateTotals(tipOutData.daily.am);
    const pmTotals = calculateTotals(tipOutData.daily.pm);
    
    return {
      netSales: amTotals.netSales + pmTotals.netSales,
      bar1Percent: amTotals.bar1Percent + pmTotals.bar1Percent,
      bus1_5Percent: amTotals.bus1_5Percent + pmTotals.bus1_5Percent,
      expoAmount: amTotals.expoAmount + pmTotals.expoAmount,
      totalTipOut: amTotals.totalTipOut + pmTotals.totalTipOut,
    };
  };

  // Calculate combined totals for EVENT 1 and EVENT 2
  const calculateEventCombinedTotals = () => {
    const event1Totals = calculateTotals(tipOutData.event.event1);
    const event2Totals = calculateTotals(tipOutData.event.event2);
    
    return {
      netSales: event1Totals.netSales + event2Totals.netSales,
      bar1Percent: event1Totals.bar1Percent + event2Totals.bar1Percent,
      bus1_5Percent: event1Totals.bus1_5Percent + event2Totals.bus1_5Percent,
      expoAmount: event1Totals.expoAmount + event2Totals.expoAmount,
      totalTipOut: event1Totals.totalTipOut + event2Totals.totalTipOut,
    };
  };

  // Inline form handlers
  const handleInlineFormChange = (shift: 'AM' | 'PM' | 'event1' | 'event2', field: string, value: string) => {
    setInlineFormData(prev => {
      const currentShiftData = prev[shift] || {
        type: 'Server',
        employee: '',
        netSales: '',
        bar1Percent: '',
        bus1_5Percent: '',
        expo: '0',
      };

      const updatedData = {
        ...currentShiftData,
        [field]: value
      };

      // Auto-calculate bar 1% and bus 1.5% when netSales changes
      if (field === 'netSales') {
        const netSalesValue = parseFloat(value) || 0;
        if (netSalesValue > 0) {
          updatedData.bar1Percent = calculateBar1Percent(netSalesValue).toString();
          updatedData.bus1_5Percent = calculateBus1_5Percent(netSalesValue).toString();
        } else {
          updatedData.bar1Percent = '';
          updatedData.bus1_5Percent = '';
        }
      }

      return {
        ...prev,
        [shift]: updatedData
      };
    });
  };

  const handleAddInlineEntry = (shift: 'AM' | 'PM' | 'event1' | 'event2') => {
    const formData = inlineFormData[shift] || {
      type: 'Server',
      employee: '',
      netSales: '',
      bar1Percent: '',
      bus1_5Percent: '',
      expo: '0',
    };
    if (!formData.employee || !formData.netSales || !formData.bar1Percent || !formData.bus1_5Percent || !formData.expo) {
      showError('Please fill in all required fields');
      return;
    }

    const netSales = parseFloat(formData.netSales);
    const expo = parseFloat(formData.expo);
    const bar1Percent = parseFloat(formData.bar1Percent);
    const bus1_5Percent = parseFloat(formData.bus1_5Percent);
    const totalTipOut = bar1Percent + bus1_5Percent + expo;

    const newEntry: TipOutEntry = {
      id: Date.now().toString(),
      employeeName: formData.employee,
      position: formData.type,
      netSales: netSales,
      bar1Percent: bar1Percent,
      bus1_5Percent: bus1_5Percent,
      expoAmount: expo,
      totalTipOut: totalTipOut,
      shift: shift === 'AM' || shift === 'PM' ? shift : 'AM', // Default to AM for events
      section: shift === 'AM' || shift === 'PM' ? 'Daily' : 'Event',
    };

    setTipOutData(prev => {
      if (shift === 'AM' || shift === 'PM') {
        return {
          ...prev,
          daily: {
            ...prev.daily,
            [shift.toLowerCase()]: [...prev.daily[shift.toLowerCase() as 'am' | 'pm'], newEntry]
          }
        };
      } else {
        return {
          ...prev,
          event: {
            ...prev.event,
            [shift]: [...prev.event[shift as 'event1' | 'event2'], newEntry]
          }
        };
      }
    });

    // Reset form
    setInlineFormData(prev => ({
      ...prev,
      [shift]: {
        type: 'Server',
        employee: '',
        netSales: '',
        bar1Percent: '',
        bus1_5Percent: '',
        expo: '0',
      }
    }));

    showSuccess('Entry added successfully!');
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddEntry = () => {
    setEditingEntry(null);
    setFormData({
      employeeName: '',
      position: 'Server',
      netSales: '',
      bar1Percent: '',
      bus1_5Percent: '',
      expoAmount: '',
      shift: 'AM',
      section: 'Daily',
    });
    setDialogOpen(true);
  };

  const handleEditEntry = (entry: TipOutEntry) => {
    setEditingEntry(entry);
    setFormData({
      employeeName: entry.employeeName,
      position: entry.position,
      netSales: entry.netSales.toString(),
      bar1Percent: entry.bar1Percent.toString(),
      bus1_5Percent: entry.bus1_5Percent.toString(),
      expoAmount: entry.expoAmount.toString(),
      shift: entry.shift,
      section: entry.section,
    });
    setDialogOpen(true);
  };


  const handleSaveEntry = async () => {
    if (!formData.employeeName.trim() || !formData.netSales || !formData.bar1Percent || !formData.bus1_5Percent || !formData.expoAmount) {
      showError('Please fill in all required fields');
      return;
    }

    setDialogLoading(true);

    try {
      const totalTipOut = parseFloat(formData.bar1Percent) + parseFloat(formData.bus1_5Percent) + parseFloat(formData.expoAmount);
      
      const entryData: TipOutEntry = {
        id: editingEntry?.id || Date.now().toString(),
        employeeName: formData.employeeName.trim(),
        position: formData.position,
        netSales: parseFloat(formData.netSales),
        bar1Percent: parseFloat(formData.bar1Percent),
        bus1_5Percent: parseFloat(formData.bus1_5Percent),
        expoAmount: parseFloat(formData.expoAmount),
        totalTipOut: totalTipOut,
        shift: formData.shift,
        section: formData.section,
      };

      setTipOutData(prev => {
        const newData = { ...prev };
        
        if (editingEntry) {
          // Remove old entry
          if (editingEntry.section === 'Daily') {
            newData.daily[editingEntry.shift.toLowerCase() as 'am' | 'pm'] = newData.daily[editingEntry.shift.toLowerCase() as 'am' | 'pm'].filter(entry => entry.id !== editingEntry.id);
          } else {
            // For events, we need to find which event array it was in
            newData.event.event1 = newData.event.event1.filter(entry => entry.id !== editingEntry.id);
            newData.event.event2 = newData.event.event2.filter(entry => entry.id !== editingEntry.id);
          }
        }
        
        // Add new entry
        if (entryData.section === 'Daily') {
          newData.daily[entryData.shift.toLowerCase() as 'am' | 'pm'].push(entryData);
        } else {
          // For events, we'll add to event1 by default (this will be handled by the dialog)
          newData.event.event1.push(entryData);
        }
        
        return newData;
      });

      showSuccess('Entry saved successfully!');
      setDialogOpen(false);
    } catch (err) {
      showError('Failed to save entry');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEntry(null);
    setFormData({
      employeeName: '',
      position: 'Server',
      netSales: '',
      bar1Percent: '',
      bus1_5Percent: '',
      expoAmount: '',
      shift: 'AM',
      section: 'Daily',
    });
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format date string (YYYY-MM-DD) to locale string
  // Parse the date components and create a date in local timezone to avoid timezone shift
  const formatDateString = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderInlineForm = (shift: 'AM' | 'PM' | 'event1' | 'event2') => {
    const formData = inlineFormData[shift] || {
      type: 'Server',
      employee: '',
      netSales: '',
      bar1Percent: '',
      bus1_5Percent: '',
      expo: '0',
    };
    const netSales = parseFloat(formData.netSales || '0') || 0;
    const expo = parseFloat(formData.expo || '0') || 0;
    const bar1Percent = parseFloat(formData.bar1Percent || '0') || 0;
    const bus1_5Percent = parseFloat(formData.bus1_5Percent || '0') || 0;
    const totalTipOut = bar1Percent + bus1_5Percent + expo;

    return (
      <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
          Add New Entry
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={(e) => handleInlineFormChange(shift, 'type', e.target.value)}
              >
                {positions.map((position) => (
                  <MenuItem key={position} value={position}>
                    {position}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Employee</InputLabel>
              <Select
                value={formData.employee}
                label="Employee"
                onChange={(e) => handleInlineFormChange(shift, 'employee', e.target.value)}
                disabled={employeesLoading}
              >
                {employeesLoading ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      Loading employees...
                    </Typography>
                  </MenuItem>
                ) : employees.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No employees found
                    </Typography>
                  </MenuItem>
                ) : (
                  employees.map((employee) => (
                    <MenuItem key={employee} value={employee}>
                      {`${employee} (${employeeNameToCode[employee] ?? '0'})`}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              size="small"
              label="Net Sales"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={formData.netSales}
              onChange={(e) => handleInlineFormChange(shift, 'netSales', e.target.value)}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
              }}
            />
          </Grid>
          <Grid item xs={12} sm={1}>
            <TextField
              fullWidth
              size="small"
              label="Bar 1%"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={formData.bar1Percent}
              onChange={(e) => handleInlineFormChange(shift, 'bar1Percent', e.target.value)}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
              }}
            />
          </Grid>
          <Grid item xs={12} sm={1}>
            <TextField
              fullWidth
              size="small"
              label="Bus 1.5%"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={formData.bus1_5Percent}
              onChange={(e) => handleInlineFormChange(shift, 'bus1_5Percent', e.target.value)}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
              }}
            />
          </Grid>
          <Grid item xs={12} sm={1}>
            <TextField
              fullWidth
              size="small"
              label="Expo"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={formData.expo}
              onChange={(e) => handleInlineFormChange(shift, 'expo', e.target.value)}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
              }}
            />
          </Grid>
          <Grid item xs={12} sm={1}>
            <TextField
              fullWidth
              size="small"
              label="Total"
              value={netSales > 0 || expo > 0 ? `$${totalTipOut.toFixed(2)}` : '$0.00'}
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { color: 'primary.main', fontWeight: 'bold' } }}
            />
          </Grid>
          <Grid item xs={12} sm={1}>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleAddInlineEntry(shift)}
              disabled={!formData.employee || !formData.netSales || !formData.expo}
              sx={{ minWidth: 80 }}
            >
              Add
            </Button>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  // Render combined total row for AM and PM
  const renderCombinedTotalRow = () => {
    const combinedTotals = calculateCombinedTotals();
    const hasEntries = tipOutData.daily.am.length > 0 || tipOutData.daily.pm.length > 0;

    if (!hasEntries) return null;

    return (
      <TableContainer component={Paper} sx={{ mb: 2, border: TIPOUT_BORDER }}>
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableBody>
            <TableRow sx={{
              backgroundColor: 'success.main',
              '& .MuiTableCell-root': {
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                border: '2px solid',
                borderColor: 'success.dark',
              }
            }}>
              <TableCell sx={{ width: TIPOUT_COLUMN_WIDTHS[0] }}>
                <Typography variant="h6" fontWeight="bold" color="white">
                  DAILY TOTAL (AM + PM)
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ width: TIPOUT_COLUMN_WIDTHS[1] }}>
                <Typography variant="h6" fontWeight="bold" color="white">
                  ${combinedTotals.netSales.toFixed(2)}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ width: TIPOUT_COLUMN_WIDTHS[2] }}>
                <Typography variant="h6" fontWeight="bold" color="white">
                  ${combinedTotals.bar1Percent}.00
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ width: TIPOUT_COLUMN_WIDTHS[3] }}>
                <Typography variant="h6" fontWeight="bold" color="white">
                  ${combinedTotals.bus1_5Percent}.00
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ width: TIPOUT_COLUMN_WIDTHS[4] }}>
                <Typography variant="h6" fontWeight="bold" color="white">
                  ${combinedTotals.expoAmount.toFixed(2)}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ width: TIPOUT_COLUMN_WIDTHS[5] }}>
                <Typography variant="h6" fontWeight="bold" color="white">
                  ${combinedTotals.totalTipOut.toFixed(2)}
                </Typography>
              </TableCell>
              <TableCell align="center" sx={{ width: TIPOUT_COLUMN_WIDTHS[6] }}>
                {/* Empty cell for actions column */}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render combined total row for EVENT 1 and EVENT 2
  const renderEventCombinedTotalRow = () => {
    const combinedTotals = calculateEventCombinedTotals();
    const hasEntries = tipOutData.event.event1.length > 0 || tipOutData.event.event2.length > 0;

    if (!hasEntries) return null;

    return (
      <TableContainer component={Paper} sx={{ mb: 2, border: TIPOUT_BORDER }}>
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableBody>
            <TableRow sx={{
              backgroundColor: 'warning.main',
              '& .MuiTableCell-root': {
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                border: '2px solid',
                borderColor: 'warning.dark',
              }
            }}>
              <TableCell sx={{ width: TIPOUT_COLUMN_WIDTHS[0] }}>
                <Typography variant="h6" fontWeight="bold" color="white">
                  EVENT TOTAL (EVENT 1 + EVENT 2)
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ width: TIPOUT_COLUMN_WIDTHS[1] }}>
                <Typography variant="h6" fontWeight="bold" color="white">
                  ${combinedTotals.netSales.toFixed(2)}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ width: TIPOUT_COLUMN_WIDTHS[2] }}>
                <Typography variant="h6" fontWeight="bold" color="white">
                  ${combinedTotals.bar1Percent}.00
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ width: TIPOUT_COLUMN_WIDTHS[3] }}>
                <Typography variant="h6" fontWeight="bold" color="white">
                  ${combinedTotals.bus1_5Percent}.00
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ width: TIPOUT_COLUMN_WIDTHS[4] }}>
                <Typography variant="h6" fontWeight="bold" color="white">
                  ${combinedTotals.expoAmount.toFixed(2)}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ width: TIPOUT_COLUMN_WIDTHS[5] }}>
                <Typography variant="h6" fontWeight="bold" color="white">
                  ${combinedTotals.totalTipOut.toFixed(2)}
                </Typography>
              </TableCell>
              <TableCell align="center" sx={{ width: TIPOUT_COLUMN_WIDTHS[6] }}>
                {/* Empty cell for actions column */}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderTipOutTable = (entries: TipOutEntry[], section: 'Daily' | 'Event', shift?: 'AM' | 'PM') => {
    const servers = entries.filter(entry => entry.position === 'Server');
    const hosts = entries.filter(entry => entry.position === 'Host');
    const bar = entries.filter(entry => entry.position === 'Bar');
    const totals = calculateTotals(entries);

    const headerCellSx = {
      color: '#fff',
      fontWeight: 700,
      fontSize: '0.8rem',
      letterSpacing: '0.05em',
      textTransform: 'uppercase' as const,
      border: TIPOUT_BORDER,
      py: 0.75,
      px: 1.5,
    };
    const sectionHeaderCellSx = {
      border: TIPOUT_BODY_BORDER, py: 0.4, px: 1.5,
      backgroundColor: section === 'Daily' ? '#c8e6c9' : '#f0f0f0',
    };

    return (
      <TableContainer component={Paper} sx={{ mb: 2, border: TIPOUT_BORDER }}>
        <Table size="small" sx={{ borderCollapse: 'collapse', tableLayout: 'fixed', '& td': { border: TIPOUT_BODY_BORDER, py: 0.5, px: 1.5 } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#b71c1c' }}>
              <TableCell sx={{ ...headerCellSx, width: TIPOUT_COLUMN_WIDTHS[0] }}>Employee</TableCell>
              <TableCell align="right" sx={{ ...headerCellSx, width: TIPOUT_COLUMN_WIDTHS[1] }}>Net Sales</TableCell>
              <TableCell align="right" sx={{ ...headerCellSx, width: TIPOUT_COLUMN_WIDTHS[2] }}>Bar 1%</TableCell>
              <TableCell align="right" sx={{ ...headerCellSx, width: TIPOUT_COLUMN_WIDTHS[3] }}>Bus 1.5%</TableCell>
              <TableCell align="right" sx={{ ...headerCellSx, width: TIPOUT_COLUMN_WIDTHS[4] }}>Expo $3/$4 F/S</TableCell>
              <TableCell align="right" sx={{ ...headerCellSx, width: TIPOUT_COLUMN_WIDTHS[5] }}>Total Tip Out</TableCell>
              <TableCell align="center" sx={{ ...headerCellSx, width: TIPOUT_COLUMN_WIDTHS[6] }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Servers Section */}
            {servers.length > 0 && (
              <>
                <TableRow>
                  <TableCell colSpan={7} sx={sectionHeaderCellSx}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={section === 'Daily' ? { fontSize: '18px', color: '#000080' } : undefined}>
                      Servers
                    </Typography>
                  </TableCell>
                </TableRow>
                {servers.map((entry, idx) => {
                  const isEditing = editingRowId === entry.id;
                  const hasUnsavedChanges = unsavedChanges.has(entry.id);
                  const currentData = isEditing && tempRowData ? tempRowData : entry;

                  return (
                    <TableRow
                      key={entry.id}
                      hover
                      sx={{
                        backgroundColor: hasUnsavedChanges
                          ? 'rgba(255, 193, 7, 0.1)'
                          : (section === 'Daily' && idx % 2 === 1 ? '#f0f0f0' : 'inherit'),
                        borderLeft: hasUnsavedChanges ? '4px solid #ffc107' : 'none',
                      }}
                    >
                      <TableCell>
                        {isEditing ? (
                          <FormControl fullWidth size="small">
                            <InputLabel>Employee</InputLabel>
                            <Select
                              value={currentData.employeeName}
                              label="Employee"
                              onChange={(e) => handleTempDataChange('employeeName', e.target.value)}
                            >
                              {employees.map((employee) => (
                                <MenuItem key={employee} value={employee}>
                                  {`${employee} (${employeeNameToCode[employee] ?? '0'})`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography component="span" sx={{ fontWeight: section === 'Daily' ? 700 : 400 }}>
                            {entry.employeeName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={currentData.netSales}
                            onChange={(e) => handleTempDataChange('netSales', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 100 }}
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>
                            }}
                          />
                        ) : (
                          `$${entry.netSales.toFixed(2)}`
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={currentData.bar1Percent}
                            onChange={(e) => handleTempDataChange('bar1Percent', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 80 }}
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                            }}
                          />
                        ) : (
                          `$${entry.bar1Percent.toFixed(2)}`
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={currentData.bus1_5Percent}
                            onChange={(e) => handleTempDataChange('bus1_5Percent', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 80 }}
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                            }}
                          />
                        ) : (
                          `$${entry.bus1_5Percent.toFixed(2)}`
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={currentData.expoAmount}
                            onChange={(e) => handleTempDataChange('expoAmount', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 80 }}
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                            }}
                          />
                        ) : (
                          `$${entry.expoAmount.toFixed(2)}`
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          ${currentData.totalTipOut.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {isEditing ? (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleSaveEdit(entry.id)}
                              color="success"
                            >
                              <SaveIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleCancelEdit}
                              color="error"
                            >
                              <CancelIcon />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleStartEdit(entry)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteEntry(entry.id, section, shift)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}

            {/* Host Section */}
            {hosts.length > 0 && (
              <>
                <TableRow>
                  <TableCell colSpan={7} sx={sectionHeaderCellSx}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={section === 'Daily' ? { fontSize: '18px', color: '#000080' } : undefined}>
                      Host
                    </Typography>
                  </TableCell>
                </TableRow>
                {hosts.map((entry, idx) => {
                  const isEditing = editingRowId === entry.id;
                  const hasUnsavedChanges = unsavedChanges.has(entry.id);
                  const currentData = isEditing && tempRowData ? tempRowData : entry;

                  return (
                    <TableRow
                      key={entry.id}
                      hover
                      sx={{
                        backgroundColor: hasUnsavedChanges
                          ? 'rgba(255, 193, 7, 0.1)'
                          : (section === 'Daily' && idx % 2 === 1 ? '#f0f0f0' : 'inherit'),
                        borderLeft: hasUnsavedChanges ? '4px solid #ffc107' : 'none',
                      }}
                    >
                      <TableCell>
                        {isEditing ? (
                          <FormControl fullWidth size="small">
                            <InputLabel>Employee</InputLabel>
                            <Select
                              value={currentData.employeeName}
                              label="Employee"
                              onChange={(e) => handleTempDataChange('employeeName', e.target.value)}
                            >
                              {employees.map((employee) => (
                                <MenuItem key={employee} value={employee}>
                                  {`${employee} (${employeeNameToCode[employee] ?? '0'})`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography component="span" sx={{ fontWeight: section === 'Daily' ? 700 : 400 }}>
                            {entry.employeeName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={currentData.netSales}
                            onChange={(e) => handleTempDataChange('netSales', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 100 }}
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>
                            }}
                          />
                        ) : (
                          `$${entry.netSales.toFixed(2)}`
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={currentData.bar1Percent}
                            onChange={(e) => handleTempDataChange('bar1Percent', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 80 }}
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                            }}
                          />
                        ) : (
                          `$${entry.bar1Percent.toFixed(2)}`
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={currentData.bus1_5Percent}
                            onChange={(e) => handleTempDataChange('bus1_5Percent', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 80 }}
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                            }}
                          />
                        ) : (
                          `$${entry.bus1_5Percent.toFixed(2)}`
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={currentData.expoAmount}
                            onChange={(e) => handleTempDataChange('expoAmount', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 80 }}
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                            }}
                          />
                        ) : (
                          `$${entry.expoAmount.toFixed(2)}`
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          ${currentData.totalTipOut.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {isEditing ? (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleSaveEdit(entry.id)}
                              color="success"
                            >
                              <SaveIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleCancelEdit}
                              color="error"
                            >
                              <CancelIcon />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleStartEdit(entry)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteEntry(entry.id, section, shift)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}

            {/* Bar Section */}
            {bar.length > 0 && (
              <>
                <TableRow>
                  <TableCell colSpan={7} sx={sectionHeaderCellSx}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={section === 'Daily' ? { fontSize: '18px', color: '#000080' } : undefined}>
                      Bar
                    </Typography>
                  </TableCell>
                </TableRow>
                {bar.map((entry, idx) => {
                  const isEditing = editingRowId === entry.id;
                  const hasUnsavedChanges = unsavedChanges.has(entry.id);
                  const currentData = isEditing && tempRowData ? tempRowData : entry;

                  return (
                    <TableRow
                      key={entry.id}
                      hover
                      sx={{
                        backgroundColor: hasUnsavedChanges
                          ? 'rgba(255, 193, 7, 0.1)'
                          : (section === 'Daily' && idx % 2 === 1 ? '#f0f0f0' : 'inherit'),
                        borderLeft: hasUnsavedChanges ? '4px solid #ffc107' : 'none',
                      }}
                    >
                      <TableCell>
                        {isEditing ? (
                          <FormControl fullWidth size="small">
                            <InputLabel>Employee</InputLabel>
                            <Select
                              value={currentData.employeeName}
                              label="Employee"
                              onChange={(e) => handleTempDataChange('employeeName', e.target.value)}
                            >
                              {employees.map((employee) => (
                                <MenuItem key={employee} value={employee}>
                                  {`${employee} (${employeeNameToCode[employee] ?? '0'})`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography component="span" sx={{ fontWeight: section === 'Daily' ? 700 : 400 }}>
                            {entry.employeeName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={currentData.netSales}
                            onChange={(e) => handleTempDataChange('netSales', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 100 }}
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>
                            }}
                          />
                        ) : (
                          `$${entry.netSales.toFixed(2)}`
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={currentData.bar1Percent}
                            onChange={(e) => handleTempDataChange('bar1Percent', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 80 }}
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                            }}
                          />
                        ) : (
                          `$${entry.bar1Percent.toFixed(2)}`
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={currentData.bus1_5Percent}
                            onChange={(e) => handleTempDataChange('bus1_5Percent', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 80 }}
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                            }}
                          />
                        ) : (
                          `$${entry.bus1_5Percent.toFixed(2)}`
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={currentData.expoAmount}
                            onChange={(e) => handleTempDataChange('expoAmount', parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 80 }}
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                            }}
                          />
                        ) : (
                          `$${entry.expoAmount.toFixed(2)}`
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          ${currentData.totalTipOut.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {isEditing ? (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleSaveEdit(entry.id)}
                              color="success"
                            >
                              <SaveIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleCancelEdit}
                              color="error"
                            >
                              <CancelIcon />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleStartEdit(entry)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteEntry(entry.id, section, shift)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}

            {entries.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, border: TIPOUT_BODY_BORDER }}>
                  <Typography variant="body2" color="text.secondary">
                    No entries yet. Fill the data and Click "Add" to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {/* Total Row */}
            {entries.length > 0 && (
              <TableRow sx={{
                backgroundColor: '#b71c1c',
                '& .MuiTableCell-root': {
                  color: 'white',
                  fontWeight: 'bold',
                  border: TIPOUT_BORDER,
                }
              }}>
                <TableCell>
                  <Typography variant="subtitle1" fontWeight="bold" color="white">
                    TOTAL
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle1" fontWeight="bold" color="white">
                    ${totals.netSales.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle1" fontWeight="bold" color="white">
                    ${totals.bar1Percent}.00
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle1" fontWeight="bold" color="white">
                    ${totals.bus1_5Percent}.00
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle1" fontWeight="bold" color="white">
                    ${totals.expoAmount.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle1" fontWeight="bold" color="white">
                    ${totals.totalTipOut.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {/* Empty cell for actions column */}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Date and Save Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h6" sx={{ color: '#7f0000', fontWeight: 700, flex: '0 0 auto', whiteSpace: 'nowrap' }}>
          Date: {dateLoading ? (
            <CircularProgress size={16} sx={{ ml: 1 }} />
          ) : currentDate ? (
            formatDateString(currentDate)
          ) : (
            getCurrentDate()
          )}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Tip Out Log Form
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '0 0 auto' }}>
          <Typography
            variant="body2"
            color={autoSaveStatus === 'error' ? 'error' : 'text.secondary'}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: autoSaveStatus === 'error' ? 'bold' : undefined }}
          >
            {autoSaveStatus === 'saving' && (<><CircularProgress size={14} />Saving…</>)}
            {autoSaveStatus === 'pending' && 'Unsaved changes…'}
            {autoSaveStatus === 'saved' && 'All changes saved'}
            {autoSaveStatus === 'error' && (autoSaveError || 'Failed to save changes')}
          </Typography>
          <Button
            variant="contained"
            startIcon={saveLoading ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSaveDaily}
            disabled={saveLoading}
            sx={{ minWidth: 120 }}
          >
            {saveLoading ? 'Saving...' : 'Save Daily'}
          </Button>
        </Box>
      </Box>

      {/* Loading indicator for data */}
      {dataLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={40} />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading existing tip-out data...
          </Typography>
        </Box>
      )}

      {/* Tabs Layout */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', '& .MuiTabs-indicator': { backgroundColor: '#7f0000' } }}
        >
          <Tab label="Daily" sx={{ fontWeight: 700, color: 'text.secondary', '&.Mui-selected': { color: '#7f0000' } }} />
          <Tab label="Event" sx={{ fontWeight: 700, color: 'text.secondary', '&.Mui-selected': { color: '#7f0000' } }} />
        </Tabs>
        
        {/* Daily Tab Content */}
        <MuiBox role="tabpanel" hidden={activeTab !== 0}>
          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              {/* AM Shift */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 700 }}>
                  AM
                </Typography>
                {renderTipOutTable(tipOutData.daily.am, 'Daily', 'AM')}
                {renderInlineForm('AM')}
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {/* PM Shift */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 700 }}>
                  PM
                </Typography>
                {renderTipOutTable(tipOutData.daily.pm, 'Daily', 'PM')}
                {renderInlineForm('PM')}
              </Box>
              
              {/* Combined Total Row */}
              {renderCombinedTotalRow()}
            </Box>
          )}
        </MuiBox>
        
        {/* Event Tab Content */}
        <MuiBox role="tabpanel" hidden={activeTab !== 1}>
          {activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              {/* EVENT 1 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  EVENT 1
                </Typography>
                {renderTipOutTable(tipOutData.event.event1, 'Event', 'event1')}
                {renderInlineForm('event1')}
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {/* EVENT 2 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  EVENT 2
                </Typography>
                {renderTipOutTable(tipOutData.event.event2, 'Event', 'event2')}
                {renderInlineForm('event2')}
              </Box>
              
              {/* Combined Total Row */}
              {renderEventCombinedTotalRow()}
              
            </Box>
          )}
        </MuiBox>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEntry ? 'Edit Entry' : 'Add New Entry'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Employee</InputLabel>
                <Select
                  value={formData.employeeName}
                  label="Employee"
                  onChange={(e) => handleInputChange('employeeName', e.target.value)}
                  disabled={!!editingEntry || employeesLoading}
                >
                  {employeesLoading ? (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        Loading employees...
                      </Typography>
                    </MenuItem>
                  ) : employees.length === 0 ? (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        No employees found
                      </Typography>
                    </MenuItem>
                  ) : (
                    employees.map((employee) => (
                      <MenuItem key={employee} value={employee}>
                        {`${employee} (${employeeNameToCode[employee] ?? '0'})`}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Position</InputLabel>
                <Select
                  value={formData.position}
                  label="Position"
                  onChange={(e) => handleInputChange('position', e.target.value)}
                  disabled={!!editingEntry}
                >
                  {positions.map((position) => (
                    <MenuItem key={position} value={position}>
                      {position}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Net Sales"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={formData.netSales}
                onChange={(e) => handleInputChange('netSales', e.target.value)}
                required
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bar 1%"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={formData.bar1Percent}
                onChange={(e) => handleInputChange('bar1Percent', e.target.value)}
                required
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bus 1.5%"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={formData.bus1_5Percent}
                onChange={(e) => handleInputChange('bus1_5Percent', e.target.value)}
                required
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expo $3/$4 F/S"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={formData.expoAmount}
                onChange={(e) => handleInputChange('expoAmount', e.target.value)}
                required
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Section</InputLabel>
                <Select
                  value={formData.section}
                  label="Section"
                  onChange={(e) => handleInputChange('section', e.target.value)}
                  disabled={!!editingEntry}
                >
                  {sections.map((section) => (
                    <MenuItem key={section} value={section}>
                      {section}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {formData.section === 'Daily' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Shift</InputLabel>
                  <Select
                    value={formData.shift}
                    label="Shift"
                    onChange={(e) => handleInputChange('shift', e.target.value)}
                    disabled={!!editingEntry}
                  >
                    {shifts.map((shift) => (
                      <MenuItem key={shift} value={shift}>
                        {shift}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEntry}
            variant="contained"
            startIcon={dialogLoading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={dialogLoading}
          >
            {editingEntry ? 'Update' : 'Add'} Entry
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TipOutLogForm;
